import { useGetVacancies, type Vacancy } from '@entities/vacancy';
import { PaginateType } from '@shared/types';
import { useMemo } from 'react';
import { TableFilters } from '@shared/ui/DataTable/table-filters';
import type { TableFilterField } from '@shared/ui';
import { vacancyFiltersConfig, initialValues } from './constants';
import type { VacancyFiltersForm, VacancyFiltersQuery } from './types';

type VacancyListFiltersProps = {
  pageSize?: number;
  config?: TableFilterField[];
  setFilter: (filter: VacancyFiltersQuery) => void;
};

export const VacancyListFilters = ({
  config = vacancyFiltersConfig,
  setFilter,
  pageSize,
}: VacancyListFiltersProps) => {
  // Employee options must include people whose vacancies are all archived.
  const { data } = useGetVacancies({
    filter: {
      status: {
        items: [
          'selection_in_progress',
          'selection_suspended',
          'selection_cancelled',
          'selection_completed',
          'archived',
        ],
        operator: 'or',
      },
    },
    paginate: PaginateType.NONE,
    enabled: config.some(field => field.type === 'select-recruiter'),
  });
  const resolvedConfig = useMemo(() => {
    const vacancies: Vacancy[] = Array.isArray(data)
      ? data
      : (data?.data ?? []);
    const users = new Map<string, { value: string; label: string }>();
    vacancies.forEach(({ responsibleUser }) => {
      const value = responsibleUser.id.toString();
      users.set(value, {
        value,
        label: `${responsibleUser.profile.firstName} ${responsibleUser.profile.lastName}`,
      });
    });
    return config.map((field): TableFilterField =>
      field.type === 'select-recruiter'
        ? {
            ...field,
            type: 'select',
            searchable: true,
            options: [...users.values()],
          }
        : field
    );
  }, [config, data]);

  return (
    <TableFilters<VacancyFiltersForm>
      config={resolvedConfig}
      initialValues={initialValues}
      setFilter={setFilter}
      pageSize={pageSize}
    />
  );
};
